import { useMemo } from "react";
import { MaterialReactTableProps, MRT_ColumnDef } from "material-react-table";
import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { PageInfoDto } from "../models/domain/pagination/PageInfoDto";
import { MrtControlState, useMrt, UseMrtParams } from "./useMrt";
import { ApiResourceListParams } from "./useApiClient";
import { isFunction } from "../core/utils";

export type ApiResourceTableColumnFactory<T extends Record<string, any>> = () => MRT_ColumnDef<T>[];

export type ResourceTableApiRequestFunc<T extends Record<string, any>, TQueryExtraParams extends {} = {}> =
  (extraParams?: TQueryExtraParams) => Promise<PageInfoDto<T>>;

export type ResourceTablePagedApiRequestFunc<T extends Record<string, any>, TQueryExtraParams extends {} = {}> =
  (params: ApiResourceListParams, extraParams?: TQueryExtraParams) => Promise<PageInfoDto<T>>;

export type ApiResourceTableQueryKeyFunc<TQueryExtraParams extends {} = {}> =
  (mrtState: MrtControlState, extraParams?: TQueryExtraParams) => QueryKey

export type ApiResourceTableQueryFunc<T extends Record<string, any> = {}, TQueryExtraParams extends {} = {}> =
  (mrtState: MrtControlState, extraParams?: TQueryExtraParams) => Promise<PageInfoDto<T>>;

export type UseApiResourceTableParamsBase<T extends Record<string, any>, TQueryExtraParams extends {} = {}> = {
  columnFactory: ApiResourceTableColumnFactory<T>;
  queryKeyFn: () => string;
  queryExtraParams?: (() => TQueryExtraParams) | (TQueryExtraParams);
  queryOptions?: Omit<UseQueryOptions<PageInfoDto<T>>, "queryKey" | "queryFn">;
  mrtParams?: UseMrtParams;
}

export type UsePagedApiResourceTableParams<T extends Record<string, any>, TQueryExtraParams extends {} = {}> =
  UseApiResourceTableParamsBase<T, TQueryExtraParams> &
  {
    requestFn: ResourceTablePagedApiRequestFunc<T, TQueryExtraParams>;
  };

export type UseApiResourceTableParams<T extends Record<string, any>, TQueryExtraParams extends {} = {}> =
  UseApiResourceTableParamsBase<T, TQueryExtraParams> &
  {
    requestFn: ResourceTableApiRequestFunc<T, TQueryExtraParams>;
  };

export type ApiResourceTableState<T extends Record<string, any>> =
  & MrtControlState
  & {
  overrideData?: PageInfoDto<T>;
  query: UseQueryResult<PageInfoDto<T>>;
  columns: MRT_ColumnDef<T>[];
  muiProps?: Pick<MaterialReactTableProps<T>, "muiTableBodyRowProps">;
  tableProps?: Pick<MaterialReactTableProps<T>,
    | "state"
    | "enableRowSelection"
    | "enableMultiRowSelection"
    | "enableMultiSort"
    | "onRowSelectionChange"
    | "getRowId"
    | "manualFiltering"
    | "manualPagination"
    | "manualSorting"
    | "maxMultiSortColCount"
    | "muiToolbarAlertBannerProps"
    | "positionToolbarAlertBanner"
    | "muiPaginationProps"
  >
};

const useApiResourceTableInternal =
  <T extends Record<string, any>, TQueryExtraParams extends {}>
  (
    params: UseApiResourceTableParamsBase<T, TQueryExtraParams>,
    tableQueryKeyFn: ApiResourceTableQueryKeyFunc<TQueryExtraParams>,
    tableQueryFn: ApiResourceTableQueryFunc<T, TQueryExtraParams>,
  ): ApiResourceTableState<T> => {
    const {
      columnFactory,
      queryKeyFn,
      queryExtraParams,
      queryOptions,
      mrtParams
    } = params;

    const mrtState = useMrt(mrtParams);
    const extraParams = isFunction(queryExtraParams) ? queryExtraParams() : queryExtraParams;

    const query = useQuery<PageInfoDto<T>>({
      queryKey: [
        queryKeyFn(),
        ...tableQueryKeyFn(mrtState, extraParams)
      ],
      queryFn: async () => {
        return tableQueryFn(mrtState, extraParams);
      },
      ...queryOptions
    });

    const columns = useMemo<MRT_ColumnDef<T>[]>(columnFactory, [columnFactory]);

    return {
      ...mrtState,
      query,
      columns
    };
  }

export const usePagedApiResourceTable =
  <T extends Record<string, any>, TQueryExtraParams extends {} = {}>
  (params: UsePagedApiResourceTableParams<T, TQueryExtraParams>): ApiResourceTableState<T> => {
    const {
      requestFn,
      ...baseParams
    } = params

    return useApiResourceTableInternal(
      baseParams,
      mrtState => [
        mrtState.columnFilters,
        mrtState.globalFilter,
        mrtState.pagination.pageIndex,
        mrtState.pagination.pageSize,
        mrtState.sorting,
      ],
      (mrtState, extraParams) => {
        const params: ApiResourceListParams = {
          page: mrtState.pagination.pageIndex,
          pageSize: mrtState.pagination.pageSize,
          filters: JSON.stringify(mrtState.columnFilters ?? []),
          globalFilter: mrtState.globalFilter ?? '',
          sorting: JSON.stringify(mrtState.sorting ?? []),
        };

        return requestFn(params, extraParams);
      });
  }

export const useApiResourceTable =
  <T extends Record<string, any>, TQueryExtraParams extends {} = {}>
  (params: UseApiResourceTableParams<T, TQueryExtraParams>): ApiResourceTableState<T> => {
    const {
      requestFn,
      ...baseParams
    } = params;

    return useApiResourceTableInternal(
      baseParams,
      _ => [],
      (_, extraParams) => requestFn(extraParams)
    );
  }